import { useRef, useMemo, useEffect, forwardRef } from 'react';
import { v1 as uuidv1 } from 'uuid';
import {
  Card,
  CardMedia,
  Dialog,
  DialogContent,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { video } from '../../../services/MediaRegistrationService';
import './VideoPlayer.scss';
import ObjectUtils from '../../../utils/ObjectUtils';
import { filterUndefined } from '../../../utils/FilterUndefinedObject';

/**
 * This is a fix for ios that the video cannot be automatically played.
 * The video can loaded when the user has interaction with the software e.g. tap the screen.
 * Use this method to preload the video when the user click, tap a button.
 * */
export const preloadVideo = (videoUrl) => {
  video.src = videoUrl;
  video.muted = true;
  video.play().catch(() => {
    // Do nothing
  });
  const timerPlay = setTimeout(() => {
    video.pause();
    clearTimeout(timerPlay);
  }, 250);
};

/**
 * Video player
 */
const VideoPlayer = forwardRef(({
  ...props
}, ref) => {
  const defaultProps = {
    videoUrl: null,
    open: false,
    handleClose: () => { },
    onInlineVideoPlay: () => { },
    onInlineVideoStop: () => { },
    onInlineVideoError: () => { },
    playsVideoInline: false,
    videoPoster: null,
    onDialogVideoEnd: () => { },
    forPrinting: false,
  };
  const {
    videoUrl,
    open,
    handleClose,
    playsVideoInline,
    onInlineVideoPlay,
    onInlineVideoStop,
    onInlineVideoError,
    videoPoster,
    onDialogVideoEnd,
    forPrinting,
  } = { ...defaultProps, ...filterUndefined(props) };
  const videoRefEl = useRef(null);
  const dialogRef = useRef(null);
  const closeButtonRef = useRef(null);
  const hasRoutedInitialTabRef = useRef(false);

  const isMuiFocusTrapElement = (element) => {
    if (!element || !element.getAttribute || !element.classList) {
      return false;
    }
    const testId = element.getAttribute('data-testid');
    return testId === 'sentinelStart'
      || testId === 'sentinelEnd'
      || element.classList.contains('MuiDialog-container')
      || element.classList.contains('MuiBackdrop-root');
  };

  useEffect(() => {
    if (!open || playsVideoInline) {
      return undefined;
    }
    hasRoutedInitialTabRef.current = false;

    const handleFocusIn = (event) => {
      const root = dialogRef.current;
      if (!root || !root.contains(event.target) || !isMuiFocusTrapElement(event.target)) {
        return;
      }
      // Do not auto-focus close when dialog opens; wait for first Tab.
      if (!hasRoutedInitialTabRef.current) {
        return;
      }

      const testId = event.target.getAttribute ? event.target.getAttribute('data-testid') : '';
      if (testId === 'sentinelStart') {
        closeButtonRef.current?.focus();
        return;
      }

      closeButtonRef.current?.focus();
    };

    const handleInitialTab = (event) => {
      if (event.key !== 'Tab' || hasRoutedInitialTabRef.current) {
        return;
      }

      const root = dialogRef.current;
      if (!root) {
        return;
      }

      const { activeElement } = document;
      if (activeElement === closeButtonRef.current) {
        hasRoutedInitialTabRef.current = true;
        return;
      }
      if (activeElement && root.contains(activeElement) && !isMuiFocusTrapElement(activeElement)) {
        hasRoutedInitialTabRef.current = true;
        return;
      }

      event.preventDefault();
      closeButtonRef.current?.focus();
      hasRoutedInitialTabRef.current = true;
    };

    document.addEventListener('focusin', handleFocusIn, true);
    window.addEventListener('keydown', handleInitialTab, true);
    return () => {
      document.removeEventListener('focusin', handleFocusIn, true);
      window.removeEventListener('keydown', handleInitialTab, true);
    };
  }, [open, playsVideoInline]);

  const handleCloseFunction = (e) => {
    if (e) {
      e.stopPropagation();
    }
    video.src = null;
    handleClose();
  };

  const handleEntered = () => {
    video.src = videoUrl;
    video.muted = false;
    video.tabIndex = 1;
    video.play().catch(() => {
      // Do nothing
    });
    videoRefEl.current.appendChild(video);
    video.onended = function onseeked() {
      ObjectUtils.setTimeout(() => {
        handleCloseFunction();
        onDialogVideoEnd();
      }, 2000);
    };
  };

  const inlineVideoId = useMemo(() => uuidv1(), []);

  return (
    playsVideoInline ? (
      <div className='video-player-inline-video-container'>
        <CardMedia
          id={inlineVideoId}
          className='video-player-inline-video'
          component='video'
          src={videoUrl}
          autoPlay={!forPrinting}
          muted
          playsInline
          controls
          onPlay={onInlineVideoPlay}
          onEnded={onInlineVideoStop}
          onPause={onInlineVideoStop}
          onError={onInlineVideoError}
          poster={videoPoster}
          ref={ref}
        />
      </div>
    ) : (
      <Dialog
        ref={dialogRef}
        open={open}
        onClose={handleCloseFunction}
        className='video-player-dialog'
        TransitionProps={{
          onEntered: handleEntered,
        }}
        data-test='video-player-dialog'
        tabIndex={-1}
      >
        {
          videoUrl ? (
            <DialogContent className='video-player-dialog-content'>
              <IconButton
                ref={closeButtonRef}
                className='video-player-close-button'
                onClick={handleCloseFunction}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.stopPropagation();
                    e.preventDefault();
                    handleCloseFunction();
                  }
                }}
                // eslint-disable-next-line jsx-a11y/tabindex-no-positive
                tabIndex='1'
              >
                <CloseIcon />
              </IconButton>
              <Card ref={videoRefEl} className='video-player-dialog-card' />
            </DialogContent>
          ) : ''
        }
      </Dialog>
    )
  );
});

export default VideoPlayer;
